/**
 * Central helper for the Starter/Premium/Professional subscription tier - originally added
 * under #2491 keyed off `subscriptionKey`, now upgraded for #2470's `category` attribute.
 *
 * ASSUMPTION (documented here because the backend attribute doesn't exist yet at the time this
 * was written): once #2470 ships, `subscriptionAssignment.subscriptionType.category` (and so
 * `subscriptionType.category` from GET /subscriptions) is expected to carry
 * 'STARTER' | 'PREMIUM' | 'PROFESSIONAL'. The point of the attribute (per the ticket) is that
 * future custom subscription types can carry their own display `name`/`subscriptionKey` while
 * still declaring one of these three as a base category - so `category`, not `name` or
 * `subscriptionKey`, is the one thing every tier check in the app should read going forward.
 *
 * Until the backend ships `category`, it's derived here from the existing `subscriptionKey`
 * prefix (e.g. 'STARTER_03_2023' -> STARTER, 'PROFESSIONAL_TESTING_12_2024' -> PROFESSIONAL) or
 * `name`/`subscriptionName` ('Professional' -> PROFESSIONAL), so nothing about current gating
 * behaviour changes before the attribute exists. Once `category` is present on the data it wins
 * automatically - no call site needs to change again.
 *
 * `subscription` here can be either a raw `subscriptionType` object (has `category`,
 * `subscriptionKey`, `name`, as returned by GET /subscriptions) or one of the flattened
 * "currentSubscription"/"nextSubscription" view-model objects built in Main.js/Dashboard.js/
 * YourSubscription.js (has `category`, `subscriptionKey`, `subscriptionName` directly, no
 * nested `subscriptionType`) - every shape used for subscription tier checks across the app.
 */

export const SUBSCRIPTION_CATEGORY = {
  STARTER: 'STARTER',
  PREMIUM: 'PREMIUM',
  PROFESSIONAL: 'PROFESSIONAL',
};

export const SUBSCRIPTION_TIER_RANK = {
  [SUBSCRIPTION_CATEGORY.STARTER]: 0,
  [SUBSCRIPTION_CATEGORY.PREMIUM]: 1,
  [SUBSCRIPTION_CATEGORY.PROFESSIONAL]: 2,
};

const isKnownTier = (value) => Object.prototype.hasOwnProperty.call(SUBSCRIPTION_TIER_RANK, value);

export const getSubscriptionTier = (subscription) => {
  const category = subscription?.category?.toString().toUpperCase();
  if (isKnownTier(category)) return category;

  // Fallback only, until backend ships `category` - see module comment above.
  const keyPrefix = subscription?.subscriptionKey?.toString().toUpperCase().split('_')[0];
  if (isKnownTier(keyPrefix)) return keyPrefix;

  const name = (subscription?.name || subscription?.subscriptionName)?.toString().toUpperCase();
  if (isKnownTier(name)) return name;

  return undefined;
};

export const isStarterTier = (subscription) => getSubscriptionTier(subscription) === SUBSCRIPTION_CATEGORY.STARTER;
export const isPremiumTier = (subscription) => getSubscriptionTier(subscription) === SUBSCRIPTION_CATEGORY.PREMIUM;
export const isProfessionalTier = (subscription) =>
  getSubscriptionTier(subscription) === SUBSCRIPTION_CATEGORY.PROFESSIONAL;

// Rank for comparing tiers (e.g. telling an upgrade from a downgrade). `undefined` for an
// unrecognized/missing tier, same as an unknown key in SUBSCRIPTION_TIER_RANK always was.
export const getSubscriptionTierRank = (subscription) => SUBSCRIPTION_TIER_RANK[getSubscriptionTier(subscription)];

export const hasMinimumSubscriptionTier = (currentSubscription, minimumTier) => {
  const tier = getSubscriptionTier(currentSubscription);
  return tier !== undefined && SUBSCRIPTION_TIER_RANK[tier] >= SUBSCRIPTION_TIER_RANK[minimumTier];
};

// Property-level flags that only work at or above a given subscription tier.
// If a flag is left `true` on a property after the account drops below the
// tier it requires (e.g. a Professional trial ending), it needs to be reset.
export const PROPERTY_FEATURE_MIN_TIER = {
  isPropertyOwnerInfoEnabled: 'PROFESSIONAL',
  isPropertyOwnerInfoInMessagesEnabled: 'PROFESSIONAL',
  isWegModeActive: 'PROFESSIONAL',
};
